import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Typography, Form, Input, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  login as apiLogin,
  register as apiRegister,
  resetPassword,
  sendPasswordResetCode,
  sendRegistrationCode,
} from '../services/api';
import { getDeviceInfo } from '../utils/device';

const { Title, Text, Link } = Typography;
const normalizeEmailInput = (value) => String(value || '')
  .replace(/[\u200B-\u200D\uFEFF]/g, '')
  .trim()
  .toLowerCase();

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login: doLogin, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [codeLoading, setCodeLoading] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [registerEmail, setRegisterEmail] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [resetCodeLoading, setResetCodeLoading] = useState(false);
  const [loginForm] = Form.useForm();
  const [registerForm] = Form.useForm();
  const [resetForm] = Form.useForm();
  const emailInputRef = useRef(null);
  const resetEmailInputRef = useRef(null);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      doLogin(token).then(() => navigate('/', { replace: true }));
    }
  }, [searchParams, doLogin, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const showLoginForm = () => {
    loginForm.resetFields();
    registerForm.resetFields();
    resetForm.resetFields();
    setRegisterEmail('');
    setResetEmail('');
    setIsRegister(false);
    setIsResetPassword(false);
  };

  const showRegisterForm = () => {
    loginForm.resetFields();
    resetForm.resetFields();
    setResetEmail('');
    registerForm.resetFields();
    setRegisterEmail('');
    setIsResetPassword(false);
    setIsRegister(true);
  };

  const showResetPasswordForm = () => {
    loginForm.resetFields();
    registerForm.resetFields();
    setRegisterEmail('');
    resetForm.resetFields();
    setResetEmail('');
    setIsRegister(false);
    setIsResetPassword(true);
  };

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const res = await apiLogin({ ...values, deviceInfo: getDeviceInfo() });
      if (res.data.success) {
        await doLogin(res.data.data.token);
        message.success('登录成功！');
        navigate('/', { replace: true });
      }
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.message || '登录失败';
      if (data?.code === 'DEVICE_PENDING') {
        message.warning(msg);
      } else if (data?.code === 'DEVICE_BLOCKED') {
        message.error(msg);
      } else {
        message.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const res = await apiRegister({
        email: normalizeEmailInput(values.registerEmail),
        password: values.registerPassword,
        name: values.name,
        verificationCode: values.registerVerificationCode,
      });
      if (res.data.success) {
        message.success(res.data.message || '注册申请已提交，请等待管理员审批');
        showLoginForm();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '注册失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRegistrationCode = async () => {
    try {
      const email = normalizeEmailInput(registerEmail || registerForm.getFieldValue('registerEmail') || (emailInputRef.current?.input?.value));
      if (!email) {
        message.error('请先填写邮箱');
        return;
      }
      if (!email.endsWith('@welzek.com')) {
        message.error('仅支持 @welzek.com 邮箱注册');
        return;
      }
      registerForm.setFieldsValue({ registerEmail: email });
      setCodeLoading(true);
      const res = await sendRegistrationCode({ email });
      if (res.data.success) {
        message.success(res.data.message || '验证码已发送，请查看企业邮箱');
      }
    } catch (err) {
      if (err?.errorFields) return;
      const msg = err.response?.data?.message || '验证码发送失败';
      message.error(msg);
    } finally {
      setCodeLoading(false);
    }
  };

  const handleSendPasswordResetCode = async () => {
    try {
      const email = normalizeEmailInput(resetEmail || resetForm.getFieldValue('resetEmail') || (resetEmailInputRef.current?.input?.value));
      if (!email) {
        message.error('请先填写邮箱');
        return;
      }
      if (!email.endsWith('@welzek.com')) {
        message.error('仅支持 @welzek.com 邮箱找回密码');
        return;
      }
      resetForm.setFieldsValue({ resetEmail: email });
      setResetCodeLoading(true);
      const res = await sendPasswordResetCode({ email });
      if (res.data.success) {
        message.success(res.data.message || '如果该邮箱为已注册员工账号，验证码将发送到企业邮箱');
      }
    } catch (err) {
      const msg = err.response?.data?.message || '验证码发送失败';
      message.error(msg);
    } finally {
      setResetCodeLoading(false);
    }
  };

  const handleResetPassword = async (values) => {
    setLoading(true);
    try {
      const res = await resetPassword({
        email: normalizeEmailInput(values.resetEmail),
        verificationCode: values.passwordResetCode,
        newPassword: values.passwordResetNewPassword,
      });
      if (res.data.success) {
        message.success(res.data.message || '密码已重置，请使用新密码登录');
        showLoginForm();
      }
    } catch (err) {
      const msg = err.response?.data?.message || '密码重置失败';
      message.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-layout">
        <div className="login-hero">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, marginBottom: 'clamp(18px, 4vh, 30px)' }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 12px 28px rgba(1, 79, 162, 0.14)',
              border: '1px solid rgba(1, 79, 162, 0.10)',
            }}
          >
            <img src="/welzek-logo.svg" alt="为准电子" style={{ width: 34, display: 'block' }} />
          </div>
          <div>
            <Text strong style={{ color: '#014fa2', fontSize: 18 }}>为准电子</Text>
            <div style={{ color: '#61708a', fontSize: 12, marginTop: 2 }}>Welzek Version Management</div>
          </div>
        </div>

        <div className="page-kicker">无线测试设备版本管理</div>
        <Title
          level={1}
          style={{
            margin: 0,
            maxWidth: 620,
            color: '#172033',
            fontSize: 'clamp(30px, 3.1vw, 42px)',
            lineHeight: 1.18,
            fontWeight: 800,
          }}
        >
          知无不为，匠心求准
        </Title>
        <Text
          style={{
            display: 'block',
            marginTop: 16,
            maxWidth: 560,
            color: '#61708a',
            fontSize: 15,
            lineHeight: 1.65,
          }}
        >
          面向 T6290 与 T8290 的版本上传、发布追踪与下载管理。
        </Text>
        <div
          className="soft-panel"
          style={{
            marginTop: 'clamp(18px, 4vh, 30px)',
            maxWidth: 560,
            padding: 14,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 12,
          }}
        >
          {[
            ['Release', '发布版本'],
            ['Daily', '每日构建'],
            ['Market', '市场版本'],
          ].map(([label, desc]) => (
            <div key={label} style={{ padding: 12, borderRadius: 8, background: '#fff' }}>
              <Text strong style={{ color: '#014fa2', fontSize: 18 }}>{label}</Text>
              <div style={{ color: '#61708a', fontSize: 12, marginTop: 4 }}>{desc}</div>
            </div>
          ))}
        </div>
      </div>

        <Card
          className="content-card login-card"
          style={{
            width: '100%',
            borderRadius: 8,
            boxShadow: '0 18px 48px rgba(1, 79, 162, 0.16)',
          }}
        >
        <div style={{ marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0, color: '#172033', fontWeight: 800 }}>
            {isRegister ? '创建员工账号' : isResetPassword ? '找回密码' : '登录系统'}
          </Title>
          <Text style={{ display: 'block', marginTop: 8, color: '#61708a', fontSize: 13 }}>
            仅支持 @welzek.com 企业邮箱
          </Text>
        </div>

        {isRegister ? (
          <Form
            key="register-form"
            layout="vertical"
            form={registerForm}
            onFinish={handleRegister}
            autoComplete="off"
          >
            <Form.Item
              label="姓名"
              name="name"
              rules={[{ required: true, message: '请输入姓名' }]}
            >
              <Input placeholder="请输入姓名" size="large" />
            </Form.Item>
            <Form.Item
              label="邮箱"
              name="registerEmail"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' },
                {
                  validator: (_, value) => {
                    if (!value || normalizeEmailInput(value).endsWith('@welzek.com')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('仅支持 @welzek.com 邮箱注册'));
                  },
                },
              ]}
              normalize={normalizeEmailInput}
            >
              <Input
                placeholder="name@welzek.com"
                size="large"
                ref={emailInputRef}
                autoComplete="username"
                onChange={(event) => setRegisterEmail(normalizeEmailInput(event.target.value))}
              />
            </Form.Item>
            <Form.Item label="邮箱验证码">
              <div style={{ display: 'flex', gap: 8 }}>
                <Form.Item
                  name="registerVerificationCode"
                  noStyle
                  rules={[
                    { required: true, message: '请输入邮箱验证码' },
                    { len: 6, message: '验证码为6位数字' },
                  ]}
                >
                  <Input placeholder="请输入6位验证码" size="large" autoComplete="one-time-code" />
                </Form.Item>
                <Button
                  size="large"
                  loading={codeLoading}
                  onClick={handleSendRegistrationCode}
                  style={{ minWidth: 116 }}
                >
                  获取验证码
                </Button>
              </div>
            </Form.Item>
            <Form.Item
              label="密码"
              name="registerPassword"
              rules={[
                { required: true, message: '请输入密码' },
                { min: 6, message: '密码长度至少6位' },
              ]}
            >
              <Input.Password placeholder="请输入密码" size="large" autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="确认密码"
              name="confirmRegisterPassword"
              dependencies={['registerPassword']}
              rules={[
                { required: true, message: '请确认密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('registerPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="请再次输入密码" size="large" autoComplete="new-password" />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              style={{
                height: 44,
                fontSize: 15,
                fontWeight: 700,
                border: 'none',
              }}
            >
              注册
            </Button>
          </Form>
        ) : isResetPassword ? (
          <Form
            key="reset-password-form"
            layout="vertical"
            form={resetForm}
            onFinish={handleResetPassword}
            autoComplete="off"
          >
            <Form.Item
              label="邮箱"
              name="resetEmail"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' },
                {
                  validator: (_, value) => {
                    if (!value || normalizeEmailInput(value).endsWith('@welzek.com')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('仅支持 @welzek.com 邮箱找回密码'));
                  },
                },
              ]}
              normalize={normalizeEmailInput}
            >
              <Input
                placeholder="name@welzek.com"
                size="large"
                ref={resetEmailInputRef}
                autoComplete="username"
                onChange={(event) => setResetEmail(normalizeEmailInput(event.target.value))}
              />
            </Form.Item>
            <Form.Item label="邮箱验证码">
              <div style={{ display: 'flex', gap: 8 }}>
                <Form.Item
                  name="passwordResetCode"
                  noStyle
                  rules={[
                    { required: true, message: '请输入邮箱验证码' },
                    { len: 6, message: '验证码为6位数字' },
                  ]}
                >
                  <Input placeholder="请输入6位验证码" size="large" autoComplete="one-time-code" />
                </Form.Item>
                <Button
                  size="large"
                  loading={resetCodeLoading}
                  onClick={handleSendPasswordResetCode}
                  style={{ minWidth: 116 }}
                >
                  获取验证码
                </Button>
              </div>
            </Form.Item>
            <Form.Item
              label="新密码"
              name="passwordResetNewPassword"
              rules={[
                { required: true, message: '请输入新密码' },
                { min: 6, message: '密码长度至少6位' },
              ]}
            >
              <Input.Password placeholder="请输入新密码" size="large" autoComplete="new-password" />
            </Form.Item>
            <Form.Item
              label="确认新密码"
              name="confirmPasswordResetNewPassword"
              dependencies={['passwordResetNewPassword']}
              rules={[
                { required: true, message: '请确认新密码' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('passwordResetNewPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('两次输入的密码不一致'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="请再次输入新密码" size="large" autoComplete="new-password" />
            </Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              style={{
                height: 44,
                fontSize: 15,
                fontWeight: 700,
                border: 'none',
              }}
            >
              重置密码
            </Button>
          </Form>
        ) : (
          <Form
            key="login-form"
            layout="vertical"
            form={loginForm}
            onFinish={handleLogin}
            autoComplete="on"
          >
            <Form.Item
              label="邮箱"
              name="email"
              rules={[
                { required: true, message: '请输入邮箱' },
                { type: 'email', message: '请输入有效的邮箱地址' },
                {
                  validator: (_, value) => {
                    if (!value || normalizeEmailInput(value).endsWith('@welzek.com')) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('仅支持 @welzek.com 邮箱登录'));
                  },
                },
              ]}
              normalize={normalizeEmailInput}
            >
              <Input placeholder="name@welzek.com" size="large" autoComplete="username" />
            </Form.Item>
            <Form.Item
              label="密码"
              name="password"
              rules={[{ required: true, message: '请输入密码' }]}
            >
              <Input.Password placeholder="请输入密码" size="large" autoComplete="current-password" />
            </Form.Item>
            <div style={{ marginTop: -8, marginBottom: 16, textAlign: 'right' }}>
              <Link onClick={showResetPasswordForm}>忘记密码？</Link>
            </div>
            <Button
              type="primary"
              htmlType="submit"
              size="large"
              block
              loading={loading}
              style={{
                height: 44,
                fontSize: 15,
                fontWeight: 700,
                border: 'none',
              }}
            >
              登录
            </Button>
          </Form>
        )}

        <div style={{ marginTop: 16, textAlign: 'center' }}>
          {isRegister ? (
            <Text type="secondary">
              已有账号？<Link onClick={showLoginForm}>去登录</Link>
            </Text>
          ) : isResetPassword ? (
            <Text type="secondary">
              想起密码？<Link onClick={showLoginForm}>去登录</Link>
            </Text>
          ) : (
            <Text type="secondary">
              还没有账号？<Link onClick={showRegisterForm}>去注册</Link>
            </Text>
          )}
        </div>

        <div
          style={{
            marginTop: 16,
            textAlign: 'center',
            color: '#8a97aa',
            fontSize: 12,
          }}
        >
          仅支持 @welzek.com 邮箱的员工
        </div>

        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid #f0f0f0',
            textAlign: 'center',
            color: '#8a97aa',
            fontSize: 12,
          }}
        >
          知无不为 · 匠心求准
        </div>
        </Card>
      </div>
    </div>
  );
}
